import React, { JSX, useContext, useEffect, useState } from "react"
import { isEqual } from "lodash";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters";
import { QHPNetworkTypeEnum, QHPFiltersContext, QHPPrimaryFiltersPayload, QHPStarRatingTypeEnum } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters";
import { BenchmarkingCounty, BenchmarkingLineOfBusinessEnum, BenchmarkingState } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { DropdownItem, MultiselectDropdown } from "@app/shared/checkbox-dropdown";
import { QESButton, QESSelect } from "@app/shared/qes-standards";

const QHPPrimaryFilters = (): JSX.Element => {
  const [hasFilterData, setHasFilterData] = useState<boolean>(false);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const qhpFiltersContext = useContext(QHPFiltersContext);
  const featureFlagContext = useContext(FeatureFlagContext);

  useEffect(() => {
    setHasFilterData(!!benchmarkingFiltersContext.states.length);
  }, [benchmarkingFiltersContext.states]);

  const applyDisabled = (): boolean => {
    // do not allow button interactions if ANYTHING is loading
    if (benchmarkingFiltersContext.linesOfBusinessLoading || qhpFiltersContext.primaryFiltersLoading || !hasFilterData) {
      return true;
    }

    const lineOfBusinessEqual = isEqual(
      benchmarkingFiltersContext.displayedPrimaryFilters?.lineOfBusinessId,
      benchmarkingFiltersContext.selectedLineOfBusiness
    );

    const countiesEqual = isEqual(
      benchmarkingFiltersContext.displayedPrimaryFilters?.countyIds?.sort(),
      qhpFiltersContext.selectedCounties.map(x => x.key.countyId).sort()
    );

    const starRatingsEqual = isEqual(
      benchmarkingFiltersContext.displayedPrimaryFilters?.starRatings?.sort(),
      qhpFiltersContext.selectedStarRatings.map(x => x.key).sort()
    );

    const planOptionEqual = isEqual(
      (benchmarkingFiltersContext.displayedPrimaryFilters as QHPPrimaryFiltersPayload)?.networkType,
      qhpFiltersContext.selectedNetworkType
    );

    const planTypesEqual = isEqual(
      benchmarkingFiltersContext.displayedPrimaryFilters?.planTypes?.sort(),
      qhpFiltersContext.selectedPlanTypes.map(x => x.key).sort()
    );

    // in order to avoid unnecessary api calls, prevent the user from applying filters if
    // a.) the currently-selected filters are the same as those that were used for what is already displayed
    // b.) at least one county, a line of business, a star rating, and a membership count are not selected
    if ((!countiesEqual || !lineOfBusinessEqual || !starRatingsEqual || !planOptionEqual || !planTypesEqual) && (
      qhpFiltersContext.selectedCounties.length > 0 &&
      !!benchmarkingFiltersContext.selectedLineOfBusiness &&
      qhpFiltersContext.selectedStarRatings.length > 0 &&
      qhpFiltersContext.selectedPlanTypes.length > 0
    )) {
      return false;
    }

    return true;
  }

  const clearDisabled = (): boolean => {
    // restoring filters to their defaults is unnecessary if the currently-selected filters are already the defaults
    if (!benchmarkingFiltersContext.linesOfBusinessLoading && !qhpFiltersContext.primaryFiltersLoading && (
      benchmarkingFiltersContext.selectedLineOfBusiness !== BenchmarkingLineOfBusinessEnum.Medicare ||
      qhpFiltersContext.selectedCounties.length !== 0 ||
      !!qhpFiltersContext.selectedState ||
      qhpFiltersContext.selectedStarRatings.length !== qhpFiltersContext.mappedStarRatings.length ||
      qhpFiltersContext.selectedNetworkType !== QHPNetworkTypeEnum.All
    )) {
      return false;
    }

    return true;
  }

  const getDentalPlanTooltip = (): JSX.Element => {
    return (
      <div className="text-left">
        This filter allows you to filter on all networks, Qualified Health Plan (QHP) networks, or Stand-Alone Dental Plan (SADP) networks.<br />
        <ul className="mb-0">
          <li>
            Choosing &quot;Stand-Alone Dental Plan (SADP)&quot; will return networks that are dental coverage only.
          </li>
          <li>
            Choosing &quot;Qualified Health Plan (QHP)&quot; will return all networks that are used in QHPs. These networks may be medical only, a combination of medical, pharmacy, vision, and dental, or some other combination.
          </li>
          <li>
            Choosing &quot;All Networks&quot; will return all networks.
          </li>
        </ul>
      </div>
    );
  }

  const onStateChange = (stateAbbreviation: string): void => {
    const state: BenchmarkingState = benchmarkingFiltersContext.states.find(x => x.stateAbbreviation === stateAbbreviation);

    if (!!state) {
      qhpFiltersContext.setSelectedState(state);
    }
  }

  return (
    <React.Fragment>
      <div className="row">
        <div className="col-12">
          <label className="label-m mb-2">
            Filters
          </label>
        </div>
      </div>
      <div className="row">
        <div className="col">
          <QESSelect
            disabled={benchmarkingFiltersContext.linesOfBusinessLoading || !benchmarkingFiltersContext.linesOfBusiness.length || !featureFlagContext.enabledFeatureFlags.has(Features.NETWORK_BENCHMARKING_MARKET_PLACE)}
            testId="network-benchmarking-dashboard__primary-filters--line-of-business"
            label="Line of Business"
            onChange={(e) => benchmarkingFiltersContext.setSelectedLineOfBusiness(parseInt(e.target.value))}
            value={benchmarkingFiltersContext.selectedLineOfBusiness || ""}
          >
            {Object.values(benchmarkingFiltersContext.linesOfBusiness).map((lineOfBusiness, index) => (
              <option
                key={index}
                value={lineOfBusiness}
              >
                {BenchmarkingLineOfBusinessEnum[lineOfBusiness]}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <QESSelect
            disabled={qhpFiltersContext.primaryFiltersLoading || !hasFilterData}
            testId="network-benchmarking-dashboard__primary-filters--state"
            label="State"
            onChange={(e) => onStateChange(e.target.value)}
            value={qhpFiltersContext.selectedState?.stateAbbreviation || ""}
          >
            {benchmarkingFiltersContext.states.map((state, index) => (
              <option
                key={index}
                value={state.stateAbbreviation}
              >
                {state.stateName}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <MultiselectDropdown
            containerClassName="d-block"
            customSelectInputText={(!!qhpFiltersContext.counties.length && qhpFiltersContext.counties.length === qhpFiltersContext.selectedCounties.length) ? "All" : null}
            disabled={qhpFiltersContext.primaryFiltersLoading || !hasFilterData || !qhpFiltersContext.selectedState}
            items={qhpFiltersContext.counties}
            onSelectionChanged={(selectedCounties: DropdownItem<BenchmarkingCounty>[]) => { qhpFiltersContext.setSelectedCounties(selectedCounties) }}
            label="County"
            selectedItems={qhpFiltersContext.selectedCounties}
            testId="network-benchmarking-dashboard__primary-filters--county"
          />
        </div>
        <div className="col">
          <MultiselectDropdown
            containerClassName="d-block"
            customSelectInputText={(qhpFiltersContext.mappedStarRatings.length === qhpFiltersContext.selectedStarRatings.length) ? "All" : null}
            disabled={qhpFiltersContext.primaryFiltersLoading || !hasFilterData}
            items={qhpFiltersContext.mappedStarRatings}
            onSelectionChanged={(selectedStarRatings: DropdownItem<QHPStarRatingTypeEnum>[]) => { qhpFiltersContext.setSelectedStarRatings(selectedStarRatings) }}
            label="CMS Star Rating"
            selectedItems={qhpFiltersContext.selectedStarRatings}
            testId="network-benchmarking-dashboard__primary-filters--contract-star-rating"
          />
        </div>
        <div className="col">
          <MultiselectDropdown
            containerClassName="d-block"
            customSelectInputText={(qhpFiltersContext.planTypes.length === qhpFiltersContext.selectedPlanTypes.length) ? "All" : null}
            disabled={qhpFiltersContext.primaryFiltersLoading || !hasFilterData}
            items={qhpFiltersContext.planTypes}
            onSelectionChanged={(selectedPlanTypes: DropdownItem<string>[]) => { qhpFiltersContext.setSelectedPlanTypes(selectedPlanTypes) }}
            label="Plan Type"
            selectedItems={qhpFiltersContext.selectedPlanTypes}
            testId="network-benchmarking-dashboard__primary-filters--contract-plan-types"
          />
        </div>
        <div className="col">
          <QESSelect
            disabled={qhpFiltersContext.primaryFiltersLoading || !hasFilterData}
            testId="network-benchmarking-dashboard__primary-filters--network-type"
            label="Network Type"
            onChange={(e) => qhpFiltersContext.setSelectedNetworkType(e.target.value as QHPNetworkTypeEnum)}
            value={qhpFiltersContext.selectedNetworkType || ""}
            validationTooltipContent={getDentalPlanTooltip()}
            validationTooltipPosition={"bottom"}
          >
            {Object.values(QHPNetworkTypeEnum).map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col d-flex">
          <div className="align-self-center">
            <QESButton
              disabled={applyDisabled()}
              qesTestId="network-benchmarking-dashboard__primary-filters--apply"
              qesType="secondary"
              onClick={() => qhpFiltersContext.onApplyPrimaryFiltersClick()}
              className="pb-0 px-0"
            >
              Apply
            </QESButton>

            <QESButton
              disabled={clearDisabled()}
              qesTestId="network-benchmarking-dashboard__primary-filters--clear"
              qesType="secondary-danger"
              onClick={() => qhpFiltersContext.onClearPrimaryFiltersClick()}
              className="pb-0 px-0 ml-2"
            >
              Clear
            </QESButton>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default QHPPrimaryFilters;
